import type { ReactNode } from 'react';
import { AlertTriangle, Check, Info, Sparkles } from 'lucide-react';
import type { Severity } from '../../core/types';
import { cn } from '../../lib/cn';
import { SEVERITY_LABEL } from '../../lib/tone';

type Tone = Severity | 'success' | 'neutral' | 'brand';

const TONES: Record<Tone, string> = {
  critical:
    'bg-critical-50 text-critical-700 ring-critical-200 dark:bg-critical-950 dark:text-critical-100 dark:ring-critical-700',
  important:
    'bg-important-50 text-important-700 ring-important-200 dark:bg-important-950 dark:text-important-100 dark:ring-important-700',
  polish:
    'bg-polish-50 text-polish-700 ring-polish-200 dark:bg-polish-950 dark:text-polish-100 dark:ring-polish-700',
  success:
    'bg-success-50 text-success-700 ring-success-200 dark:bg-success-950 dark:text-success-100 dark:ring-success-700',
  neutral: 'bg-sunken text-muted ring-line',
  brand:
    'bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-950 dark:text-brand-100 dark:ring-brand-700',
};

/**
 * Severity is signalled by icon as well as colour.
 *
 * Around 8% of men have some form of red/green colour vision deficiency, and
 * critical/important here are red and amber. Pairing each with a distinct glyph
 * means the distinction survives.
 */
const SEVERITY_ICON: Record<Severity, ReactNode> = {
  critical: <AlertTriangle />,
  important: <Info />,
  polish: <Sparkles />,
};

export interface BadgeProps {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = 'neutral', icon, children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONES[tone],
        className
      )}
    >
      {icon ? (
        <span aria-hidden="true" className="grid place-items-center [&>svg]:size-3">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export function SeverityBadge({ severity, className }: { severity: Severity; className?: string }) {
  return (
    <Badge tone={severity} icon={SEVERITY_ICON[severity]} className={className}>
      {SEVERITY_LABEL[severity]}
    </Badge>
  );
}

export function PassBadge({ className }: { className?: string }) {
  return (
    <Badge tone="success" icon={<Check />} className={className}>
      All clear
    </Badge>
  );
}
